
import React, { useState } from "react";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { 
  Search, 
  FileText, 
  BookOpen, 
  GraduationCap, 
  Calendar, 
  Award, 
  Users, 
  Info 
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

export default function Documentation() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  
  const filterContent = (content: any[]) => {
    if (!searchTerm) return content;
    
    return content.filter(item => 
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.answer.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  // Getting Started content
  const gettingStartedGuides = [
    {
      title: "Welcome to CEU Navigator",
      icon: <BookOpen className="h-5 w-5 text-accent" />,
      description: "Learn about the platform designed to streamline continuing education for educators.",
      content: "CEU Navigator is a comprehensive platform designed specifically for Northwest Suburban Integration School District (NWSISD) educators to simplify the process of finding, registering for, and tracking continuing education units (CEUs)."
    },
    {
      title: "Setting Up Your Profile",
      icon: <Users className="h-5 w-5 text-accent" />,
      description: "Complete your profile to personalize your experience.",
      content: "Your profile contains important information that helps personalize your experience and ensures your certificates contain accurate information."
    },
    {
      title: "Understanding CEU Requirements",
      icon: <Info className="h-5 w-5 text-accent" />,
      description: "Learn about Minnesota's CEU requirements for license renewal.",
      content: "Minnesota requires teachers to complete 125 clock hours of professional development for license renewal every five years."
    }
  ];
  
  // General FAQs
  const generalFaqs = [
    {
      question: "What is CEU Navigator?",
      answer: "CEU Navigator is a platform designed for Northwest Suburban Integration School District to help educators manage, track, and complete continuing education units (CEUs). It provides tools for course registration, attendance tracking, and certificate generation."
    },
    {
      question: "How do I register for a course?",
      answer: "To register for a course, navigate to the 'Courses' page from the main dashboard. Browse available courses and click on one to view details. On the course details page, click the 'Register' button to sign up. You'll receive confirmation of your registration status."
    },
    {
      question: "How do I access my certificates?",
      answer: "Your certificates are available in the 'Certificates' section. After completing a course and having your attendance verified, certificates are automatically generated and accessible for download from this page."
    },
    {
      question: "What if a course is full?",
      answer: "If a course reaches its maximum capacity, you'll be automatically added to a waitlist when you attempt to register. The system will notify you if a spot becomes available due to cancellations."
    },
    {
      question: "How do I cancel my registration?",
      answer: "To cancel a registration, go to the 'Courses' page, find the course you've registered for, and click on it. On the course details page, click the 'Cancel Registration' button."
    },
    {
      question: "Is my information secure?",
      answer: "Yes, CEU Navigator prioritizes the security of your data. We use industry-standard encryption protocols to protect your personal information, and our database employs row-level security to ensure that users can only access information they're authorized to view."
    }
  ];
  
  // Course FAQs
  const courseFaqs = [
    {
      question: "What types of courses are offered?",
      answer: "CEU Navigator offers two types of courses: Live Events and Self-Paced Courses. Live Events have specific dates, times, and venues. Self-Paced Courses can be started and completed at your convenience."
    },
    {
      question: "How are CEU hours calculated?",
      answer: "CEU hours are pre-determined for each course based on the content and activities involved. The hours are displayed on the course details page and will be reflected on your certificates upon completion."
    },
    {
      question: "What are the state requirements for CEUs?",
      answer: "State requirements include various categories such as Behavioral Intervention, Reading Preparation, Suicide Prevention, Cultural Competency, and more. Courses are tagged with relevant requirements they fulfill, helping you track your progress towards meeting state mandates."
    },
    {
      question: "How do I know which courses I need?",
      answer: "Your dashboard shows your progress towards different state requirements. Courses are tagged with the requirements they fulfill, making it easier to identify which courses you need to complete your professional development goals."
    },
    {
      question: "Can I get a refund for a course?",
      answer: "Refund policies vary by course. Please check the specific course details for information on refunds, or contact the district office for assistance."
    },
    {
      question: "How do I find courses that match my interests?",
      answer: "The Courses page offers multiple filtering and sorting options to help you find relevant courses. You can filter by category, subject area, date range, format (live vs. self-paced), target audience, and specific state requirements."
    }
  ];
  
  // Attendance FAQs
  const attendanceFaqs = [
    {
      question: "How is attendance tracked for live events?",
      answer: "For live events, instructors will provide a check-in code during the session. Enter this code in the system to verify your attendance. Instructors may also manually verify attendance."
    },
    {
      question: "How do I mark completion for self-paced courses?",
      answer: "For self-paced courses, navigate to the course details page and click the 'Mark as Complete' button after you've finished all required content. This will trigger attendance verification and certificate generation."
    },
    {
      question: "What if I forget to check in at a live event?",
      answer: "If you forget to check in, contact the course instructor or administrator as soon as possible. They can manually verify your attendance if they can confirm you were present."
    },
    {
      question: "How long after a course ends can I still verify attendance?",
      answer: "Attendance verification should be completed within 48 hours of a live event. For self-paced courses, you can mark completion at any time after finishing the content."
    },
    {
      question: "Will I be notified when my attendance is verified?",
      answer: "Yes, you'll receive a notification when your attendance is verified, and your certificate becomes available for download."
    },
    {
      question: "What happens if I need to leave a live event early?",
      answer: "If you need to leave a live event early, inform the instructor before departing. Depending on the length of the event and how much time you attended, you may receive full, partial, or no CEU credit."
    }
  ];
  
  // Certificate FAQs
  const certificateFaqs = [
    {
      question: "How do I download my certificates?",
      answer: "Go to the 'Certificates' page from the main navigation. Find the certificate you want to download and click the 'Download' or 'Print' button. Certificates are available in PDF format."
    },
    {
      question: "When are certificates generated?",
      answer: "Certificates are automatically generated after your attendance has been verified for a course. For live events, this happens after check-in code verification. For self-paced courses, this occurs after you mark completion."
    },
    {
      question: "What information is included on certificates?",
      answer: "Certificates include your name, the course title, completion date, number of CEU hours earned, relevant state requirements fulfilled, and official district verification."
    },
    {
      question: "How long are my certificates stored in the system?",
      answer: "Your certificates are stored in the system indefinitely. However, we recommend downloading and saving copies for your personal records."
    },
    {
      question: "Can I request changes to my certificate?",
      answer: "If you notice any errors on your certificate, please contact the district office or platform administrator. They can review and reissue corrected certificates if necessary."
    },
    {
      question: "How do I use certificates for license renewal?",
      answer: "To use your certificates for license renewal, first download all relevant certificates from the platform. Create a license renewal portfolio that includes all certificates organized by requirement category."
    }
  ];
  
  // Admin FAQs
  const adminFaqs = [
    {
      question: "How do I manage course registrations?",
      answer: "Administrators can manage registrations from the 'Admin > Courses' section. Select a course to view registered participants, manage the waitlist, and verify attendance."
    },
    {
      question: "How do I create a new course?",
      answer: "To create a new course, go to 'Admin > Courses' and click the 'Create Course' button. Fill out the course details form, including title, description, hours, event type, and other required information."
    },
    {
      question: "How do I verify attendance for a live event?",
      answer: "For live events, go to 'Admin > Courses', select the course, and navigate to the 'Attendance' tab. You can view check-ins and manually verify attendance for participants."
    },
    {
      question: "How do I manage users?",
      answer: "User management is available in the 'Admin > Users' section. Here you can view all users, update their roles, and manage their account status."
    },
    {
      question: "How do I access analytics?",
      answer: "Analytics are available in the 'Analytics' section for administrators. Review data on course participation, certificate issuance, and engagement with different categories."
    },
    {
      question: "How do I manage certificates?",
      answer: "Certificate management is handled in the 'Admin > Certificates' section. This interface displays all issued certificates with comprehensive filtering options by date, course, user, school, or category."
    }
  ];
  
  // State Requirements Content
  const stateRequirementsData = [
    {
      id: "behavioral",
      title: "Positive Behavioral Intervention Strategies",
      hours: 2,
      description: "Content focuses on understanding student behavior, developing positive behavior supports, and implementing proactive approaches to classroom management."
    },
    {
      id: "reading",
      title: "Reading Preparation",
      hours: 1,
      description: "Content addresses comprehensive, scientifically-based reading instruction including instruction in phonemic awareness, phonics, fluency, vocabulary, and comprehension."
    },
    {
      id: "mentalhealth",
      title: "Key Warning Signs of Mental Illness in Children",
      hours: 1,
      description: "Content focuses on identifying early signs of mental health challenges in students and appropriate intervention strategies."
    },
    {
      id: "suicide",
      title: "Suicide Prevention Strategies",
      hours: 1,
      description: "Content addresses recognizing suicide risk factors, intervention strategies, and resources for supporting students in crisis."
    },
    {
      id: "ell",
      title: "English Language Learners",
      hours: 1,
      description: "Content focuses on effective instructional strategies for students learning English as an additional language."
    },
    {
      id: "cultural",
      title: "Cultural Competency Training",
      hours: 1,
      description: "Content addresses developing awareness, knowledge, and skills for working with diverse student populations and creating inclusive learning environments."
    }
  ];

  return (
    <div className="container pt-6 page-content">
      <div className="mx-auto max-w-5xl">
        <h1 className="text-3xl font-bold mb-2 text-primary font-title">Documentation & Help Center</h1>
        <p className="text-muted-foreground mb-8 font-body">Find comprehensive guides, answers to common questions, and learn how to get the most from the CEU Navigator platform.</p>
        
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            className="pl-10 border-accent-light/30 focus-visible:ring-primary/20 font-subtitle"
            placeholder="Search documentation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab}>
          <div className="bg-card rounded-lg shadow border border-accent-light/20 mb-8">
            <TabsList className="flex flex-wrap h-auto rounded-none border-b border-accent-light/20 space-x-0 bg-background/50">
              <TabsTrigger 
                value="general" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <Info className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger 
                value="getting-started" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <BookOpen className="h-4 w-4" />
                Getting Started
              </TabsTrigger>
              <TabsTrigger 
                value="courses" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <GraduationCap className="h-4 w-4" />
                Courses
              </TabsTrigger>
              <TabsTrigger 
                value="attendance" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <Calendar className="h-4 w-4" />
                Attendance
              </TabsTrigger>
              <TabsTrigger 
                value="certificates" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <Award className="h-4 w-4" />
                Certificates
              </TabsTrigger>
              <TabsTrigger 
                value="state-requirements" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <FileText className="h-4 w-4" />
                State Requirements
              </TabsTrigger>
              <TabsTrigger 
                value="admin" 
                className="px-4 py-3 data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none flex gap-2 text-sm font-section"
              >
                <Users className="h-4 w-4" />
                Admin
              </TabsTrigger>
            </TabsList>
            
            {/* General Tab Content */}
            <TabsContent value="general" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary font-heading font-bold">General Information</h2>
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow border border-accent-light/10">
                {filterContent(generalFaqs).map((faq, index) => (
                  <AccordionItem value={`general-${index}`} key={index}>
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent-subtle font-title">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 py-3 text-muted-foreground font-body">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
            
            {/* Getting Started Tab Content */}
            <TabsContent value="getting-started" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary font-heading font-bold">Getting Started with CEU Navigator</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {gettingStartedGuides.map((guide, index) => (
                  <Card key={index} className="border border-accent-light/20 hover:shadow-md transition-all">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2 mb-2">
                        {guide.icon}
                        <CardTitle className="text-lg font-medium font-title">{guide.title}</CardTitle>
                      </div>
                      <CardDescription className="font-subtitle text-sm">{guide.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <div className="text-sm text-muted-foreground font-body">
                        <p>{guide.content}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="border rounded-lg p-6 bg-accent-subtle border-accent-light">
                <h3 className="text-lg font-medium mb-3 text-primary flex items-center gap-2 font-heading font-bold">
                  <Info className="h-5 w-5 text-accent" />
                  Getting the Most Out of CEU Navigator
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-2 text-sm font-subheading">Recommended First Steps</h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm font-body">
                      <li>Complete your profile with accurate personal and school information</li>
                      <li>Explore the dashboard to familiarize yourself with key metrics</li>
                      <li>Review your current progress toward state requirements</li>
                      <li>Browse available courses and register for your first opportunity</li>
                      <li>Set up notification preferences to stay informed</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2 text-sm font-subheading">Pro Tips</h4>
                    <ul className="list-disc list-inside space-y-2 text-sm font-body">
                      <li>Use the calendar view to plan your professional development</li>
                      <li>Save courses to your favorites for quick access</li>
                      <li>Export your certificates regularly</li>
                      <li>Check the dashboard frequently for new course offerings</li>
                      <li>Provide feedback after completing courses</li>
                    </ul>
                  </div>
                </div>
                <Separator className="my-4 bg-accent-light/30" />
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground font-caption">Still have questions? Contact Support</div>
                  <Button size="sm" variant="outline" className="gap-2 bg-primary text-white hover:bg-primary/90 font-subtitle">
                    <BookOpen className="h-4 w-4" />
                    Contact Support
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            {/* Courses Tab Content */}
            <TabsContent value="courses" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary flex items-center gap-2 font-heading font-bold">
                <GraduationCap className="h-5 w-5 text-accent" />
                Course Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card className="border border-accent-light/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium font-title">Live Events</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground font-body">Scheduled professional development sessions with specific dates, times, and locations.</p>
                    <ul className="mt-3 text-sm list-disc list-inside space-y-1 font-body">
                      <li>In-person workshops</li>
                      <li>Virtual seminars</li>
                      <li>Conference sessions</li>
                    </ul>
                    <Badge className="mt-4 bg-accent-light text-primary" variant="outline">Requires check-in verification</Badge>
                  </CardContent>
                </Card>
                
                <Card className="border border-accent-light/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium font-title">Self-Paced Courses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground font-body">Flexible learning opportunities that can be completed at your own pace.</p>
                    <ul className="mt-3 text-sm list-disc list-inside space-y-1 font-body">
                      <li>Online modules</li>
                      <li>Recorded presentations</li>
                      <li>Reading assignments</li>
                    </ul>
                    <Badge className="mt-4 bg-accent-light text-primary" variant="outline">Self-verified completion</Badge>
                  </CardContent>
                </Card>
                
                <Card className="border border-accent-light/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-medium font-title">Course Categories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground font-body">Courses are organized into categories to help you find relevant professional development.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge variant="secondary" className="bg-secondary text-primary">Technology</Badge>
                      <Badge variant="secondary" className="bg-secondary text-primary">Leadership</Badge>
                      <Badge variant="secondary" className="bg-secondary text-primary">Curriculum</Badge>
                      <Badge variant="secondary" className="bg-secondary text-primary">Special Education</Badge>
                      <Badge variant="secondary" className="bg-secondary text-primary">Cultural Competency</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow border border-accent-light/10">
                {filterContent(courseFaqs).map((faq, index) => (
                  <AccordionItem value={`courses-${index}`} key={index}>
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent-subtle font-title">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 py-3 text-muted-foreground font-body">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
            
            {/* Attendance Tab Content */}
            <TabsContent value="attendance" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary flex items-center gap-2 font-heading font-bold">
                <Calendar className="h-5 w-5 text-accent" />
                Attendance Verification
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="border rounded-lg p-5 bg-gradient-to-b from-accent-light/30 to-accent-light/10 border-accent-light/30">
                  <h3 className="text-lg font-medium mb-3 text-primary flex items-center gap-2 font-heading font-bold">
                    <Calendar className="h-5 w-5 text-accent" />
                    Live Event Verification
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 font-body">For in-person or virtual workshops, seminars, and conferences:</p>
                  
                  <ol className="space-y-3 text-sm mb-4 font-body">
                    <li className="flex gap-3">
                      <div className="bg-accent-light text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
                      <div>
                        <p className="font-medium">Receive check-in code during event</p>
                        <p className="text-muted-foreground text-xs font-caption">The presenter will provide a unique code</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <div className="bg-accent-light text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
                      <div>
                        <p className="font-medium">Enter code in the system</p>
                        <p className="text-muted-foreground text-xs font-caption">Navigate to your dashboard or course page</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <div className="bg-accent-light text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
                      <div>
                        <p className="font-medium">System verifies attendance</p>
                        <p className="text-muted-foreground text-xs font-caption">Your attendance is recorded</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <div className="bg-accent-light text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">4</div>
                      <div>
                        <p className="font-medium">Certificate is generated</p>
                        <p className="text-muted-foreground text-xs font-caption">Available for download</p>
                      </div>
                    </li>
                  </ol>
                </div>
                
                <div className="border rounded-lg p-5 bg-gradient-to-b from-secondary/30 to-secondary/10 border-secondary/30">
                  <h3 className="text-lg font-medium mb-3 text-primary flex items-center gap-2 font-heading font-bold">
                    <BookOpen className="h-5 w-5 text-accent" />
                    Self-Paced Course Verification
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 font-body">For online modules, recorded presentations, and independent learning:</p>
                  
                  <ol className="space-y-3 text-sm mb-4 font-body">
                    <li className="flex gap-3">
                      <div className="bg-secondary text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
                      <div>
                        <p className="font-medium">Complete all course requirements</p>
                        <p className="text-muted-foreground text-xs font-caption">Work through all modules and assignments</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <div className="bg-secondary text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
                      <div>
                        <p className="font-medium">Navigate to course page</p>
                        <p className="text-muted-foreground text-xs font-caption">Find the course in your dashboard</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <div className="bg-secondary text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
                      <div>
                        <p className="font-medium">Click "Mark as Complete"</p>
                        <p className="text-muted-foreground text-xs font-caption">Confirm that you've fulfilled all requirements</p>
                      </div>
                    </li>
                    <li className="flex gap-3">
                      <div className="bg-secondary text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0 mt-0.5">4</div>
                      <div>
                        <p className="font-medium">Certificate is generated immediately</p>
                        <p className="text-muted-foreground text-xs font-caption">Available for download</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
              
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow border border-accent-light/10">
                {filterContent(attendanceFaqs).map((faq, index) => (
                  <AccordionItem value={`attendance-${index}`} key={index}>
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent-subtle font-title">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 py-3 text-muted-foreground font-body">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
            
            {/* Certificates Tab Content */}
            <TabsContent value="certificates" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary flex items-center gap-2 font-heading font-bold">
                <Award className="h-5 w-5 text-accent" />
                Certificates
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="border border-accent-light/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-title">Certificate Features</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-3">
                      <div className="mt-0.5">
                        <Award className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-subheading">Official Documentation</h4>
                        <p className="text-xs text-muted-foreground font-body">Each certificate includes NWSISD branding and digital verification.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="mt-0.5">
                        <Award className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-subheading">Complete Details</h4>
                        <p className="text-xs text-muted-foreground font-body">Includes your name, course title, completion date, hours earned, and requirements fulfilled.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="mt-0.5">
                        <Award className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-subheading">Multiple Formats</h4>
                        <p className="text-xs text-muted-foreground font-body">Download as PDF or export as images.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="mt-0.5">
                        <Award className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-subheading">Permanent Storage</h4>
                        <p className="text-xs text-muted-foreground font-body">Certificates are stored indefinitely in your account.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <div className="border rounded-lg p-5 border-accent-light/20 bg-accent-subtle/50">
                  <h3 className="font-medium mb-3 flex items-center gap-2 font-heading font-bold">
                    <FileText className="h-4 w-4 text-accent" />
                    License Renewal Submission Tips
                  </h3>
                  
                  <ol className="space-y-3 text-sm font-body">
                    <li className="flex gap-3">
                      <div className="bg-primary/10 text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0">1</div>
                      <div>
                        <p className="font-medium">Organize by Category</p>
                        <p className="text-muted-foreground text-xs font-caption">Group certificates according to state requirement categories.</p>
                      </div>
                    </li>
                    
                    <li className="flex gap-3">
                      <div className="bg-primary/10 text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0">2</div>
                      <div>
                        <p className="font-medium">Create a Cover Sheet</p>
                        <p className="text-muted-foreground text-xs font-caption">Summarize your total hours and categories fulfilled.</p>
                      </div>
                    </li>
                    
                    <li className="flex gap-3">
                      <div className="bg-primary/10 text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0">3</div>
                      <div>
                        <p className="font-medium">Include All Verification</p>
                        <p className="text-muted-foreground text-xs font-caption">Ensure each certificate includes all required signatures.</p>
                      </div>
                    </li>
                    
                    <li className="flex gap-3">
                      <div className="bg-primary/10 text-primary rounded-full h-6 w-6 flex items-center justify-center flex-shrink-0">4</div>
                      <div>
                        <p className="font-medium">Submit Complete Portfolio</p>
                        <p className="text-muted-foreground text-xs font-caption">Follow PELSB guidelines for submission.</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
              
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow border border-accent-light/10">
                {filterContent(certificateFaqs).map((faq, index) => (
                  <AccordionItem value={`certificates-${index}`} key={index}>
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent-subtle font-title">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 py-3 text-muted-foreground font-body">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
            
            {/* State Requirements Tab Content */}
            <TabsContent value="state-requirements" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary flex items-center gap-2 font-heading font-bold">
                <FileText className="h-5 w-5 text-accent" />
                Minnesota Professional Development Requirements
              </h2>
              
              <div className="mb-8">
                <div className="bg-accent-subtle rounded-lg p-5 border border-accent-light mb-6">
                  <h3 className="text-lg font-medium mb-2 text-primary font-heading font-bold">Overview</h3>
                  <p className="text-sm text-muted-foreground mb-2 font-body">
                    Minnesota requires teachers to complete 125 clock hours of professional development for license renewal every five years. 
                    These hours must include specific areas of focus as mandated by the Professional Educator Licensing and Standards Board (PELSB).
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Badge className="bg-primary">125 Total Hours Required</Badge>
                    <Badge variant="outline" className="border-primary text-primary">5-Year Renewal Cycle</Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 mb-6">
                  {stateRequirementsData.map((req) => (
                    <Card key={req.id} className="hover:shadow-md transition-all border-accent-light/20">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-base font-title">{req.title}</CardTitle>
                          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">{req.hours} hours</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground font-body">{req.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <div className="border rounded-lg p-5 border-accent-light/20 bg-accent-subtle/30">
                  <h3 className="text-base font-medium mb-3 text-primary font-heading font-bold">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-2 text-sm font-section">How To Track Requirements</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm font-body">
                        <li>Your dashboard shows progress toward each category</li>
                        <li>Course descriptions include which requirements they fulfill</li>
                        <li>Certificates show which requirements they satisfy</li>
                        <li>Use the filters on the Courses page to find courses for specific requirements</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2 text-sm font-section">Renewal Deadlines</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm font-body">
                        <li>Requirements must be completed before license expiration</li>
                        <li>Early renewal is possible up to one year before expiration</li>
                        <li>Renewal applications typically take 2-3 weeks to process</li>
                        <li>Late renewals may require additional documentation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Admin Tab Content */}
            <TabsContent value="admin" className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-primary flex items-center gap-2 font-heading font-bold">
                <Users className="h-5 w-5 text-accent" />
                Administrator Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="border border-accent-light/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-title">Administrator Tools</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <GraduationCap className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-section">Course Management</h4>
                        <p className="text-xs text-muted-foreground font-body">Create, edit, and manage professional development courses.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Users className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-section">User Management</h4>
                        <p className="text-xs text-muted-foreground font-body">Manage educator accounts, roles, and permissions.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Calendar className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-section">Attendance Verification</h4>
                        <p className="text-xs text-muted-foreground font-body">Generate check-in codes and manage attendance records.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Award className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium font-section">Certificate Management</h4>
                        <p className="text-xs text-muted-foreground font-body">Review, issue, and manage professional development certificates.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border border-accent-light/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-title">Access and Permissions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm font-body">
                      <div className="flex items-start gap-3">
                        <Badge variant="outline" className="mt-0.5 bg-primary/10 text-primary border-primary/20">Admin</Badge>
                        <p>Full access to all system features, including user management and analytics.</p>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Badge variant="outline" className="mt-0.5 bg-primary/10 text-primary border-primary/20">Staff</Badge>
                        <p>Can create and manage courses, verify attendance, and issue certificates.</p>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Badge variant="outline" className="mt-0.5 bg-primary/10 text-primary border-primary/20">Instructor</Badge>
                        <p>Limited access to manage their own courses and verify attendance.</p>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Badge variant="outline" className="mt-0.5 bg-primary/10 text-primary border-primary/20">User</Badge>
                        <p>Standard educator access for registering, attending courses, and obtaining certificates.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow border border-accent-light/10">
                {filterContent(adminFaqs).map((faq, index) => (
                  <AccordionItem value={`admin-${index}`} key={index}>
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent-subtle font-title">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 py-3 text-muted-foreground font-body">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
